import PropTypes from 'prop-types';
import * as types from 'src/types';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Checkbox from '@mui/material/Checkbox';
import moment from 'moment';
import { useRouter } from 'next/router';
import Box from '@mui/material/Box';
import { formatToISKIfNotCurrencyClaim } from 'src/helpers/currencyClaim';
import tableStyles from 'styles/table.module.css';
import getProduct from 'src/helpers/products';
import { Tag } from '@motusehf/web-ui-component-library/dist';
import Link from 'next/link';
import GavelIcon from '@mui/icons-material/GavelOutlined';
import BalanceIcon from '@mui/icons-material/Balance';
import DoNotDisturbOnIcon from '@mui/icons-material/DoNotDisturbOnOutlined';

export default function ClaimRow({ isSelected, isSelectable, claim, onClick, isDefaultRegistered }) {
  const router = useRouter();
  const handleRowClick = (e) => {
    if (e.button !== 0 || e.ctrlKey || e.metaKey) {
      return;
    }

    e.preventDefault();
    if (claim.claimId) {
      router.push(`/yfirlit/${claim.claimId}`);
    }
  };

  const claimUrl = claim.claimId ? `/yfirlit/${claim.claimId}` : '#';
  const productDetails = getProduct(claim.product);
  const translatedProduct = productDetails?.name;
  const productColor = productDetails?.color;
  return (
    <Link href={claimUrl} passHref legacyBehavior>
      <TableRow
        component="a"
        onClick={handleRowClick}
        hover={true}
        {...(isSelectable
          ? {
              role: 'checkbox',
              selected: isSelected,
              'aria-checked': isSelected,
            }
          : {})}
        sx={{
          cursor: claim.claimId ? 'pointer' : 'default',
          textDecoration: 'none',
          color: 'inherit',
        }}
      >
        {isSelectable && (
          <TableCell
            padding="checkbox"
            onClick={(e) => {
              e.stopPropagation();
              onClick(e);
            }}
            className="no-print"
          >
            <Checkbox checked={isSelected} sx={{ transform: 'scale(1.4)' }} />
          </TableCell>
        )}
        <TableCell sx={{ fontSize: '16px' }}>{moment(claim.oldestUnpaidDueDate).format('DD.MM.YYYY')}</TableCell>
        <TableCell>
          <Box
            sx={{
              display: 'inline-block',
              '@media print': { pl: '0px' },
            }}
          >
            <div>
              <Tag
                label={translatedProduct}
                color={productColor}
                fontWeight={700}
                responsive
                {...(translatedProduct === 'Upphaf löginnheimtu' && {
                  icon: <BalanceIcon sx={{ color: '#ffffff' }} />,
                  textColor: '#ffffff',
                  iconPosition: 'right',
                })}
                {...(translatedProduct === 'Löginnheimta' && {
                  icon: <GavelIcon sx={{ color: '#ffffff' }} />,
                  textColor: '#ffffff',
                  iconPosition: 'right',
                })}
              />
            </div>
          </Box>
        </TableCell>
        <TableCell>
          <Box
            sx={{
              display: 'inline-block',
              fontSize: '16px',
              '@media print': { pl: '0px' },
            }}
          >
            {claim.claimNumber}
          </Box>
        </TableCell>
        <TableCell>
          {isDefaultRegistered && (
            <Box sx={{ mb: '4px' }}>
              <Tag
                label="Skráning á vanskilaskrá"
                color="rgba(207, 167, 176, 0.4)"
                textColor="#88233a"
                fontWeight={700}
                iconSize="1.8rem"
                height={32}
                borderColor="#88233A"
                hasBorder={true}
                icon={<DoNotDisturbOnIcon sx={{ color: '#88233a' }} />}
                iconPosition="left"
              />
            </Box>
          )}
          {claim.postponedUntilDate && moment().diff(moment(claim.postponedUntilDate)) < 0 && (
            <Box component="span" sx={{ fontWeight: 400, fontSize: '16px' }}>
              Greiðist fyrir {moment(claim.postponedUntilDate).format('DD. MMMM YYYY')}
            </Box>
          )}
          {claim.agreementStatus === 'ActiveAgreement' && (
            <Box component="span" sx={{ fontWeight: 400, fontSize: '16px' }}>
              Í greiðsludreifingu
            </Box>
          )}
          {claim.agreementStatus === 'PendingProposal' && (
            <Box component="span" sx={{ fontWeight: 400, fontSize: '16px' }}>
              Í beiðni fyrir greiðsludreifingu - ósamþykkt
            </Box>
          )}
          {claim.isInBookingProcess && (
            <Box component="span" sx={{ fontWeight: 400, fontSize: '16px' }}>
              Greiðsla í vinnslu
            </Box>
          )}
          {claim.isOpen && !claim.isInBookingProcess && claim.paymentBlockingClaims.length > 0 && (
            <Box sx={{ mt: '3px', fontSize: '16px' }}>
              Þú þarft að greiða mál{' '}
              <Link href={`/yfirlit/${claim.paymentBlockingClaims[0].claimId}`} style={{ textDecoration: 'underline' }}>
                {claim.paymentBlockingClaims[0].claimNumber}
              </Link>{' '}
              fyrst
            </Box>
          )}
        </TableCell>
        <TableCell sx={{ fontSize: '16px' }} className={tableStyles.cellNumeric} data-number={claim.totalRemaining}>
          {formatToISKIfNotCurrencyClaim(claim.product, claim.totalRemaining)}
        </TableCell>
      </TableRow>
    </Link>
  );
}

ClaimRow.propTypes = {
  claim: types.claim.isRequired,
  isSelected: PropTypes.bool.isRequired,
  isSelectable: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  isDefaultRegistered: PropTypes.bool,
};
